import React, { useEffect, useState } from 'react';
import { FiTrendingUp, FiCheck } from 'react-icons/fi';
import { boardReportAPI } from '../services/api';

// CMO's scoped view of BoardReportConfig — just marketing_budget and
// marketing_salaries, gated by is_marketing_leadership on the backend
// (MarketingConfigAPIView). Deliberately does NOT expose bank_balance or
// monthly_burn_rate — those stay board-report-only (Chairman/Admin/CEO).
const MarketingConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ marketing_budget: '', marketing_salaries: '' });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const { data } = await boardReportAPI.marketingConfig.get();
      setForm({
        marketing_budget: data?.marketing_budget ?? '',
        marketing_salaries: data?.marketing_salaries ?? '',
      });
    } catch (e) {
      setMessage({ type: 'error', text: e?.response?.data?.error || 'Failed to load marketing budget.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await boardReportAPI.marketingConfig.update(form);
      setForm({
        marketing_budget: data?.marketing_budget ?? '',
        marketing_salaries: data?.marketing_salaries ?? '',
      });
      setMessage({ type: 'success', text: 'Marketing budget saved.' });
    } catch (e2) {
      setMessage({ type: 'error', text: e2?.response?.data?.error || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading marketing budget...</div>;

  return (
    <div style={{ maxWidth: 560, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <FiTrendingUp size={22} color="#4facfe" />
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Marketing Budget</h1>
      </div>
      <p style={{ color: '#888', fontSize: 13, marginBottom: 24 }}>
        Your scoped view of the board report's marketing figures. Other board-report data
        (bank balance, monthly burn rate) isn't part of this page — see the Analytics report
        for the full picture.
      </p>

      {message && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 13,
            background: message.type === 'success' ? 'rgba(67, 233, 123, 0.12)' : 'rgba(245, 87, 87, 0.12)',
            color: message.type === 'success' ? '#2f9e5e' : '#e04b4b',
            border: `1px solid ${message.type === 'success' ? 'rgba(67, 233, 123, 0.3)' : 'rgba(245, 87, 87, 0.3)'}`,
          }}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Marketing Budget (USD)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.marketing_budget}
            onChange={(e) => handleChange('marketing_budget', e.target.value)}
            placeholder="0.00"
            style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Marketing Salaries (USD)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.marketing_salaries}
            onChange={(e) => handleChange('marketing_salaries', e.target.value)}
            placeholder="0.00"
            style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
          />
        </label>

        <button
          type="submit"
          disabled={saving}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '10px 18px', borderRadius: 8, border: 'none',
            background: saving ? '#9db8f0' : '#4facfe', color: '#fff',
            fontWeight: 600, fontSize: 14, cursor: saving ? 'default' : 'pointer', alignSelf: 'flex-start',
          }}
        >
          <FiCheck size={14} />
          {saving ? 'Saving...' : 'Save'}
        </button>
      </form>
    </div>
  );
};

export default MarketingConfig;
